// THE FACE DECAL — the one thing still drawn at runtime, and the reason is
// that it is the one thing that is genuinely per player.
//
// The skull is authored geometry now: a brow ridge, eye sockets that turn back
// under it, a nose with wings, lips with volume, a jaw with an angle, ears.
// The atlas paints the skin over it — warmth on the cheekbones and the bridge,
// a colder value in the sockets and under the jaw, blood in the lips. Neither
// of those can be per player, because both are shared by every avatar in the
// world.
//
// What IS per player is a 512×384 canvas: eye shape from the preset, size and
// spacing and brow weight from the sliders, and the mark a life left. It costs
// nothing to make, it is cached by the exact parameters that made it, and the
// shader blends it over the head island only — so the creator's five sliders
// still move what they have always moved, on a head that now has a face under
// the ink instead of a card standing 4 mm proud of a cube.
//
// THE COORDINATE SPACE is the head island's own, taken through the UV seam
// (see the shader in character.ts): x = 0.5 is the midline of the face, and y
// runs 0 at the hairline to 1 under the jaw. The feature rows below are read
// straight off the cage's ring heights — brow 0.16, eye 0.30, nose 0.45,
// mouth 0.60, chin 0.75 — so the ink lands ON the forms rather than near them.

import { type Appearance, FACE_MARKS, FACE_PRESETS } from "@bebop/shared";
import * as THREE from "three";

const W = 512;
const H = 384;

/** Where the cage puts each feature, as a fraction of the decal's height. */
const BROW = 0.115;
const EYE = 0.235;
const NOSE = 0.404;
const MOUTH = 0.585;

const cache = new Map<string, THREE.CanvasTexture>();

export function faceDecal(a: Appearance): THREE.Texture | undefined {
  const key =
    `${a.face}:${a.eyeSize.toFixed(2)}:${a.eyeSpacing.toFixed(2)}:` +
    `${a.brow.toFixed(2)}:${a.marks}`;
  const hit = cache.get(key);
  if (hit) return hit;

  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d");
  if (!ctx) return undefined;

  const cx = W / 2;
  const eyeY = EYE * H;
  // Half the eye separation, as a fraction of the decal's half width. The
  // cage's own columns bracket this: at 0.22 the pupil sits over column 1,
  // which is 2.8 cm off the midline on a 15 cm face.
  const spacing = (0.15 + a.eyeSpacing * 0.14) * W;
  const eyeR = (0.043 + a.eyeSize * 0.025) * W;
  const ink = "#211d26";
  const preset = (FACE_PRESETS[a.face] ?? FACE_PRESETS[0])?.id ?? "steady";

  ctx.fillStyle = ink;
  ctx.strokeStyle = ink;
  ctx.lineCap = "round";
  ctx.lineJoin = "round";

  for (const side of [-1, 1]) {
    const ex = cx + side * spacing;
    // A tapered lid sits in the Blender orbital rim. A full white ellipse
    // used to make every preset a pair of stickers, especially in portraits.
    const opening =
      preset === "bright"
        ? 0.49
        : preset === "heavy-lidded"
          ? 0.25
          : preset === "sharp"
            ? 0.27
            : preset === "soft"
              ? 0.44
              : 0.36;
    const halfWidth = eyeR * 1.35;
    const halfHeight = eyeR * opening;
    const tilt = preset === "sharp" ? -side * eyeR * 0.14 : 0;
    const eyePath = (): void => {
      ctx.beginPath();
      ctx.moveTo(ex - halfWidth, eyeY - tilt);
      ctx.bezierCurveTo(
        ex - eyeR * 0.48,
        eyeY - halfHeight * 1.8,
        ex + eyeR * 0.65,
        eyeY - halfHeight * 1.65,
        ex + halfWidth,
        eyeY + tilt,
      );
      ctx.bezierCurveTo(
        ex + eyeR * 0.5,
        eyeY + halfHeight * 1.35,
        ex - eyeR * 0.65,
        eyeY + halfHeight * 1.4,
        ex - halfWidth,
        eyeY - tilt,
      );
      ctx.closePath();
    };
    ctx.save();
    eyePath();
    ctx.clip();
    const sclera = ctx.createLinearGradient(ex, eyeY - eyeR, ex, eyeY + eyeR);
    sclera.addColorStop(0, "#6f655c");
    sclera.addColorStop(0.5, "#c8beb0");
    sclera.addColorStop(1, "#ddd0bc");
    ctx.fillStyle = sclera;
    ctx.fillRect(ex - halfWidth, eyeY - eyeR, halfWidth * 2, eyeR * 2);
    // Iris, limbal ring and pupil remain readable without a pure-white halo.
    ctx.fillStyle = "#3d352a";
    ctx.beginPath();
    ctx.arc(ex, eyeY, eyeR * 0.55, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = "#78674a";
    ctx.beginPath();
    ctx.arc(ex, eyeY + eyeR * 0.03, eyeR * 0.43, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = "#121418";
    ctx.beginPath();
    ctx.arc(ex, eyeY, eyeR * 0.25, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = "rgba(241, 230, 208, 0.8)";
    ctx.beginPath();
    ctx.arc(ex - eyeR * 0.17, eyeY - eyeR * 0.17, eyeR * 0.085, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
    ctx.strokeStyle = "rgba(54, 34, 30, 0.65)";
    ctx.lineWidth = eyeR * 0.12;
    eyePath();
    ctx.stroke();
    // Brows: weight from the slider, tilt from the preset.
    ctx.strokeStyle = ink;
    ctx.lineWidth = (0.008 + a.brow * 0.018) * W;
    const browTilt = preset === "sharp" ? side * -0.16 : preset === "soft" ? side * 0.1 : 0;
    const drop = (a.brow - 0.5) * 0.02 * H;
    const browY = BROW * H + drop;
    ctx.beginPath();
    ctx.moveTo(ex - eyeR * 1.5, browY + eyeR * 0.22 + browTilt * eyeR * 2);
    ctx.quadraticCurveTo(
      ex,
      browY - eyeR * 0.28,
      ex + eyeR * 1.5,
      browY + eyeR * 0.1 - browTilt * eyeR * 2,
    );
    ctx.stroke();
  }

  // The nostrils and the shadow under the nose. Two small marks, because the
  // nose itself is geometry now and does not need to be drawn.
  ctx.fillStyle = "rgba(52, 40, 40, 0.42)";
  for (const side of [-1, 1]) {
    ctx.beginPath();
    ctx.ellipse(
      cx + side * W * 0.036,
      NOSE * H + H * 0.03,
      W * 0.017,
      H * 0.011,
      side * 0.3,
      0,
      Math.PI * 2,
    );
    ctx.fill();
  }

  // The mouth: a line where the lips meet, not a drawn mouth — the lips are
  // geometry and the atlas already tinted them.
  ctx.strokeStyle = "rgba(74, 45, 44, 0.7)";
  ctx.lineWidth = W * 0.011;
  ctx.beginPath();
  const mw = W * 0.095;
  if (preset === "bright" || preset === "soft") {
    ctx.moveTo(cx - mw, MOUTH * H - H * 0.006);
    ctx.quadraticCurveTo(cx, MOUTH * H + H * 0.022, cx + mw, MOUTH * H - H * 0.006);
  } else {
    ctx.moveTo(cx - mw, MOUTH * H);
    ctx.quadraticCurveTo(cx, MOUTH * H + H * 0.008, cx + mw, MOUTH * H);
  }
  ctx.stroke();

  // Marks of a life lived — always drawn after the features they cross.
  const mark = (FACE_MARKS[a.marks] ?? FACE_MARKS[0])?.id ?? "none";
  const left = cx - spacing;
  const right = cx + spacing;
  switch (mark) {
    case "brow-scar":
      ctx.strokeStyle = "#c49a83";
      ctx.lineWidth = W * 0.009;
      ctx.beginPath();
      ctx.moveTo(left - W * 0.012, BROW * H - H * 0.045);
      ctx.lineTo(left + W * 0.012, EYE * H - eyeR * 0.6);
      ctx.stroke();
      break;
    case "cheek-scar":
      ctx.strokeStyle = "#b58a72";
      ctx.lineWidth = W * 0.009;
      ctx.beginPath();
      ctx.moveTo(right + W * 0.01, EYE * H + H * 0.055);
      ctx.lineTo(right + W * 0.04, MOUTH * H - H * 0.01);
      ctx.stroke();
      break;
    case "grease":
      ctx.fillStyle = "rgba(40, 36, 34, 0.45)";
      ctx.beginPath();
      ctx.ellipse(left - W * 0.02, MOUTH * H - H * 0.06, W * 0.055, H * 0.03, -0.4, 0, Math.PI * 2);
      ctx.fill();
      break;
    case "freckles": {
      ctx.fillStyle = "rgba(122, 74, 46, 0.55)";
      const dots: [number, number][] = [
        [-0.09, 0.44],
        [-0.06, 0.41],
        [-0.035, 0.46],
        [-0.01, 0.42],
        [0.02, 0.45],
        [0.05, 0.41],
        [0.075, 0.45],
        [0.1, 0.42],
        [-0.075, 0.5],
        [0.065, 0.5],
      ];
      for (const [dx, dy] of dots) {
        ctx.beginPath();
        ctx.arc(cx + dx * W, dy * H, W * 0.007, 0, Math.PI * 2);
        ctx.fill();
      }
      break;
    }
    case "aether-burn":
      ctx.fillStyle = "rgba(96, 138, 148, 0.34)";
      ctx.beginPath();
      ctx.ellipse(right + W * 0.045, EYE * H - H * 0.02, W * 0.042, H * 0.07, 0.3, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = "rgba(58, 96, 106, 0.28)";
      ctx.beginPath();
      ctx.ellipse(right + W * 0.055, EYE * H - H * 0.04, W * 0.022, H * 0.04, 0.3, 0, Math.PI * 2);
      ctx.fill();
      break;
    default:
      break;
  }

  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  // The canvas is drawn top-row-first and the head island's v runs crown to
  // jaw, so the decal must NOT be flipped on upload. three's default is to
  // flip, and the first portrait showed exactly what that costs: brows under
  // the eyes and a mouth in the middle of a forehead.
  texture.flipY = false;
  // The decal is sampled inside one island only; clamping keeps a UV a hair
  // outside it transparent instead of wrapping ink onto the far cheek.
  texture.wrapS = THREE.ClampToEdgeWrapping;
  texture.wrapT = THREE.ClampToEdgeWrapping;
  texture.anisotropy = 4;
  cache.set(key, texture);
  return texture;
}
